import {
  type Analytics,
  AnalyticsBrowser,
  type EventProperties,
  type UserTraits,
} from "@segment/analytics-next";
import { type App, inject } from "vue";

import { setTypewriterOptions } from "#/analytics/segment";

const isDevMode = import.meta.env.DEV && !import.meta.env.VITE_SEGMENT_KEY;
const isTestMode = import.meta.env.TEST;
const writeKey = import.meta.env.VITE_SEGMENT_KEY;

const logDevEvent = (...args: unknown[]) => {
  if (isDevMode) {
    // eslint-disable-next-line no-console
    console.info("DEV tracking event send", {
      event: args[0],
      payload: args[1],
    });
  }
};

const segmentMock = {
  isMock: true,
  track: logDevEvent,
  page: logDevEvent,
  identify: logDevEvent,
  user: () => ({
    id: () => undefined,
    anonymousId: () => undefined,
  }),
} as const;

let resolveSegmentReady: ((value?: unknown) => void) | undefined = undefined;

// Don't track dev mode, test mode, or admin portal entries
const untrackModeEnabled = () => {
  const url = new URL(window.location.toString());
  const entry = url.searchParams.get("entry");

  return isDevMode || isTestMode || entry === "admin";
};

export default {
  install: async (app: App) => {
    if (untrackModeEnabled()) {
      return app.provide("segment", segmentMock);
    }

    try {
      const analytics = new AnalyticsBrowser();
      const [segment] = await analytics.load(
        {
          writeKey,
          cdnURL: "https://a.mws.com",
        },
        {
          integrations: {
            "Segment.io": {
              apiHost: "t.mws.com/v1",
              protocol: "https",
            },
          },
        },
      );

      setTypewriterOptions({ analytics });

      // Grab the segment id from the url so we can keep tracking
      const url = new URL(window.location.toString());
      const segmentUserId = url.searchParams.get("user") ?? undefined;

      let userTraits: UserTraits | undefined = undefined;

      const userClient = url.searchParams.get("client");
      if (userClient) {
        userTraits = {
          ...(userTraits || {}),
          client: userClient,
        };
      }

      const userCountry = url.searchParams.get("country");
      if (userCountry) {
        userTraits = {
          ...(userTraits || {}),
          country: userCountry,
        };
      }

      const userCurrency = url.searchParams.get("currency");
      if (userCurrency) {
        userTraits = {
          ...(userTraits || {}),
          currency: userCurrency,
        };
      }

      await segment.identify(segmentUserId, userTraits);

      app.provide("segment", segment);
      resolveSegmentReady?.();
    } catch {
      app.provide("segment", segmentMock);
      resolveSegmentReady?.();
    }
  },
  isReady: () => {
    if (untrackModeEnabled()) return Promise.resolve();

    return new Promise((resolve) => {
      resolveSegmentReady = resolve;
    });
  },
  afterInstall: async () => {
    // Remove the user id from the url once we've extracted it and identified the user.
    // This has to be done after the app is launched, otherwise the router package will add it back.
    const url = new URL(window.location.toString());
    url.searchParams.delete("user");

    window.history.replaceState({}, "", url.toString());
  },
};

type DefaultType = {
  _?: null;
};

// Check if type is comparable to default typewriter type.
// In that case we don't need a second parameter.
type IsDefaultType<A> = Exclude<A, DefaultType> | Exclude<DefaultType, A>;

export const useSegment = () => {
  const resolved = inject<Analytics>("segment");

  if (!resolved) {
    throw new Error("Tried to send event before it was loaded");
  }

  return {
    ...resolved,
    /**
     * @deprecated
     */
    oldTrack: (event: string, data: EventProperties) => {
      return resolved.track(event, data);
    },
    track: <
      A extends (data: Parameters<A>[0]) => void,
      B extends Parameters<A>[0],
    >(
      eventFn: A,
      ...eventData: IsDefaultType<B> extends null | undefined ? [] : [B]
    ) => {
      eventFn(eventData[0] || {});
    },
  };
};
